<script setup>
/* global axios */
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import MultiselectDropdown from 'shared/components/ui/MultiselectDropdown.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import EmojiInput from 'shared/components/emoji/EmojiInput.vue';
import { vOnClickOutside } from '@vueuse/components';
import { useAlert } from 'dashboard/composables';
import WhatsappTemplatePreview from './WhatsappTemplatePreview.vue';
import { useAccount } from 'dashboard/composables/useAccount';

const props = defineProps({
  show: { type: Boolean, default: false },
  inboxId: { type: [String, Number], default: null },
  flowSlug: { type: String, default: null },
});
const emit = defineEmits(['close', 'created']);

const { accountId } = useAccount();
const { t } = useI18n();

const CATEGORY_OPTIONS = [
  { id: 'MARKETING', name: 'Marketing' },
  { id: 'UTILITY', name: 'Utility' },
  { id: 'AUTHENTICATION', name: 'Authentication' },
];

const LANGUAGE_OPTIONS = [
  { id: 'pt_BR', name: 'Português (BR)' },
  { id: 'en', name: 'English' },
  { id: 'es', name: 'Español' },
];

const HEADER_OPTIONS = computed(() => [
  { id: 'NONE', name: t('WHATSAPP_TEMPLATES.CREATE.HEADER_TYPES.NONE') },
  { id: 'TEXT', name: t('WHATSAPP_TEMPLATES.CREATE.HEADER_TYPES.TEXT') },
  { id: 'IMAGE', name: t('WHATSAPP_TEMPLATES.CREATE.HEADER_TYPES.IMAGE') },
]);

const CATALOG_BUTTON_TYPES = ['CATALOG', 'SPM', 'MPM', 'CAROUSEL'];
const FIXED_TEXT_BUTTON_TYPES = [
  'ORDER_DETAILS',
  'CATALOG',
  'SPM',
  'MPM',
  'CAROUSEL',
];
const FIXED_BUTTON_TEXT = computed(() => ({
  CATALOG: t('WHATSAPP_TEMPLATES.CREATE.BUTTON_TYPES.CATALOG_BUTTON_TEXT'),
  SPM: t('WHATSAPP_TEMPLATES.CREATE.BUTTON_TYPES.SPM_BUTTON_TEXT'),
  MPM: t('WHATSAPP_TEMPLATES.CREATE.BUTTON_TYPES.MPM_BUTTON_TEXT'),
  CAROUSEL: t('WHATSAPP_TEMPLATES.CREATE.BUTTON_TYPES.SPM_BUTTON_TEXT'),
  ORDER_DETAILS: t(
    'WHATSAPP_TEMPLATES.CREATE.BUTTON_TYPES.ORDER_DETAILS_BUTTON_TEXT'
  ),
}));

const BUTTON_MODE_OPTIONS = computed(() => [
  { id: 'default', name: t('WHATSAPP_TEMPLATES.CREATE.BUTTON_TYPES.BTN_MODE_DEFAULT') },
  { id: 'url', name: t('WHATSAPP_TEMPLATES.CREATE.BUTTON_TYPES.BTN_MODE_URL') },
]);

const BUTTON_TYPE_OPTIONS = computed(() => [
  { id: 'URL', name: t('WHATSAPP_TEMPLATES.CREATE.BUTTON_TYPES.URL') },
  {
    id: 'URL_VARIABLE',
    name: t('WHATSAPP_TEMPLATES.CREATE.BUTTON_TYPES.URL_VARIABLE'),
  },
  {
    id: 'QUICK_REPLY',
    name: t('WHATSAPP_TEMPLATES.CREATE.BUTTON_TYPES.QUICK_REPLY'),
  },
  {
    id: 'PHONE_NUMBER',
    name: t('WHATSAPP_TEMPLATES.CREATE.BUTTON_TYPES.PHONE_NUMBER'),
  },
]);

const TEMPLATE_FORMAT_OPTIONS = computed(() => [
  {
    id: 'STANDARD',
    name: t('WHATSAPP_TEMPLATES.CREATE.TEMPLATE_FORMATS.STANDARD'),
  },
  { id: 'CATALOG', name: t('WHATSAPP_TEMPLATES.CREATE.BUTTON_TYPES.CATALOG') },
  {
    id: 'ORDER_DETAILS',
    name: t('WHATSAPP_TEMPLATES.CREATE.BUTTON_TYPES.ORDER_DETAILS'),
  },
  { id: 'SPM', name: t('WHATSAPP_TEMPLATES.CREATE.BUTTON_TYPES.SPM') },
  { id: 'MPM', name: t('WHATSAPP_TEMPLATES.CREATE.BUTTON_TYPES.MPM') },
  {
    id: 'CAROUSEL',
    name: t('WHATSAPP_TEMPLATES.CREATE.BUTTON_TYPES.CAROUSEL'),
  },
]);

const STEPS = computed(() => [
  {
    id: 1,
    title: t('WHATSAPP_TEMPLATES.CREATE.STEPS.CONFIG_TITLE'),
    body: t('WHATSAPP_TEMPLATES.CREATE.STEPS.CONFIG_BODY'),
  },
  {
    id: 2,
    title: t('WHATSAPP_TEMPLATES.CREATE.STEPS.CONTENT_TITLE'),
    body: t('WHATSAPP_TEMPLATES.CREATE.STEPS.CONTENT_BODY'),
  },
  {
    id: 3,
    title: t('WHATSAPP_TEMPLATES.CREATE.STEPS.BUTTONS_TITLE'),
    body: t('WHATSAPP_TEMPLATES.CREATE.STEPS.BUTTONS_BODY'),
  },
  {
    id: 4,
    title: t('WHATSAPP_TEMPLATES.CREATE.STEPS.REVIEW_TITLE'),
    body: t('WHATSAPP_TEMPLATES.CREATE.STEPS.REVIEW_BODY'),
  },
]);

const dialogRef = ref(null);
const currentStep = ref(1);
const name = ref('');
const category = ref(CATEGORY_OPTIONS[0]);
const language = ref(LANGUAGE_OPTIONS[0]);
const templateFormat = ref(TEMPLATE_FORMAT_OPTIONS.value[0]);
const body = ref('');
const bodyExamples = ref({});
const footer = ref('');
const headerFormat = ref(HEADER_OPTIONS.value[0]);
const headerText = ref('');
const headerImagePreview = ref('');
const headerHandle = ref('');
const headerImageUrl = ref('');
const buttons = ref([]);
const isSubmitting = ref(false);
const errorMessage = ref('');
const uploadInProgress = ref(false);

function reset() {
  currentStep.value = 1;
  name.value = '';
  category.value = CATEGORY_OPTIONS[0];
  language.value = LANGUAGE_OPTIONS[0];
  templateFormat.value = TEMPLATE_FORMAT_OPTIONS.value[0];
  body.value = '';
  bodyExamples.value = {};
  footer.value = '';
  headerFormat.value = HEADER_OPTIONS.value[0];
  headerText.value = '';
  headerImagePreview.value = '';
  headerHandle.value = '';
  headerImageUrl.value = '';
  buttons.value = [];
  errorMessage.value = '';
}

watch(
  () => props.show,
  val => {
    if (val) {
      reset();
      dialogRef.value?.open();
    } else {
      dialogRef.value?.close();
    }
  }
);

watch(templateFormat, format => {
  if (format.id === 'STANDARD') {
    buttons.value = [];
    headerFormat.value = HEADER_OPTIONS.value[0];
    return;
  }
  if (format.id === 'MPM') {
    headerFormat.value =
      HEADER_OPTIONS.value.find(o => o.id === 'TEXT') ||
      HEADER_OPTIONS.value[0];
  }
  const btn = { kind: format.id, text: '', url: '', example: '', phone: '', useUrl: false };
  if (format.id === 'CAROUSEL') btn.cards = [{ body: '', url: '', example: '', buttonText: '' }, { body: '', url: '', example: '', buttonText: '' }];
  buttons.value = [btn];
});

const bodyVariables = computed(() => {
  const matches = [...(body.value || '').matchAll(/\{\{(\d+)\}\}/g)].map(
    m => m[1]
  );
  return [...new Set(matches)].sort((a, b) => Number(a) - Number(b));
});

const hasOrderDetailsButton = computed(() =>
  buttons.value.some(b => b.kind === 'ORDER_DETAILS')
);
const hasCatalogTypeButton = computed(() =>
  buttons.value.some(b => CATALOG_BUTTON_TYPES.includes(b.kind))
);
const hasSpmButton = computed(() => buttons.value.some(b => b.kind === 'SPM'));
const hasMpmButton = computed(() => buttons.value.some(b => b.kind === 'MPM'));
const hasCarouselButton = computed(() =>
  buttons.value.some(b => b.kind === 'CAROUSEL')
);
const blockAddButton = computed(
  () => hasOrderDetailsButton.value || hasCatalogTypeButton.value
);

const previewHeader = computed(() => {
  if (hasSpmButton.value || hasCarouselButton.value)
    return { format: 'PRODUCT' };
  if (headerFormat.value.id === 'TEXT')
    return { format: 'TEXT', text: headerText.value };
  if (headerFormat.value.id === 'IMAGE') return { format: 'IMAGE' };
  return { format: 'NONE' };
});

const previewButtons = computed(() =>
  buttons.value
    .filter(b => b.text || FIXED_TEXT_BUTTON_TYPES.includes(b.kind))
    .map(b => ({
      type: b.kind === 'URL_VARIABLE' ? 'URL' : b.kind,
      text: b.text || FIXED_BUTTON_TEXT.value[b.kind] || '',
    }))
);

const canProceed = computed(() => {
  if (currentStep.value === 1) return !!name.value.trim();
  if (currentStep.value === 2) {
    if (!hasCarouselButton.value && !body.value.trim()) return false;
    if (!hasSpmButton.value && !hasCarouselButton.value) {
      if (headerFormat.value.id === 'TEXT' && !headerText.value.trim())
        return false;
      if (headerFormat.value.id === 'IMAGE' && !headerHandle.value)
        return false;
    }
    return bodyVariables.value.every(v => bodyExamples.value[v]);
  }
  if (currentStep.value === 3) {
    return buttons.value.every(b => {
      if (b.kind === 'CAROUSEL') {
        const cards = b.cards || [];
        if (cards.length < 2) return false;
        if (b.useUrl) return cards.every(c => !!c.buttonText?.trim() && !!c.url?.trim());
        return true;
      }
      if (FIXED_TEXT_BUTTON_TYPES.includes(b.kind)) {
        if ((b.kind === 'SPM') && b.useUrl) return !!b.text?.trim() && !!b.url?.trim();
        return true;
      }
      if (!b.text.trim()) return false;
      if (b.kind === 'URL') return !!b.url.trim();
      if (b.kind === 'URL_VARIABLE')
        return !!b.url.trim() && !!b.example?.trim();
      if (b.kind === 'PHONE_NUMBER') return !!b.phone.trim();
      return true;
    });
  }
  return true;
});

function goNext() {
  if (canProceed.value && currentStep.value < 4) currentStep.value += 1;
}
function goBack() {
  if (currentStep.value > 1) {
    currentStep.value -= 1;
  } else {
    emit('close');
  }
}

function addButton() {
  if (buttons.value.length >= 3) return;
  if (blockAddButton.value) return;
  buttons.value.push({
    kind: 'URL',
    text: '',
    url: '',
    example: '',
    phone: '',
  });
}

function onButtonKindChange(idx, newKind) {
  buttons.value[idx].kind = newKind;
  if (newKind === 'CAROUSEL' && !buttons.value[idx].cards) {
    buttons.value[idx].cards = [{ body: '', url: '', example: '', buttonText: '' }, { body: '', url: '', example: '', buttonText: '' }];
  }
}

function addCarouselCard(btnIdx) {
  const cards = buttons.value[btnIdx].cards || [];
  if (cards.length >= 10) return;
  buttons.value[btnIdx].cards = [...cards, { body: '', url: '', example: '', buttonText: '' }];
}

function removeCarouselCard(btnIdx, cardIdx) {
  const cards = buttons.value[btnIdx].cards || [];
  if (cards.length <= 2) return;
  buttons.value[btnIdx].cards = cards.filter((_, i) => i !== cardIdx);
}

function updateCarouselCard(btnIdx, cardIdx, val) {
  buttons.value[btnIdx].cards[cardIdx].body = val;
}

function removeButton(index) {
  buttons.value.splice(index, 1);
}

async function handleImageChange(event) {
  const file = event.target.files?.[0];
  if (!file) return;
  headerImagePreview.value = URL.createObjectURL(file);
  uploadInProgress.value = true;
  try {
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await axios.post(
      `/api/v1/accounts/${accountId.value}/inboxes/${props.inboxId}/whatsapp_templates/upload_media`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    headerHandle.value = data.handle;
    headerImageUrl.value = data.url || '';
  } catch (err) {
    errorMessage.value =
      err.response?.data?.error ||
      t('WHATSAPP_TEMPLATES.CREATE.ALERTS.IMAGE_ERROR');
    headerImagePreview.value = '';
  } finally {
    uploadInProgress.value = false;
  }
}

function buildPayload() {
  let headerPayload;
  if (hasSpmButton.value) {
    headerPayload = { format: 'PRODUCT' };
  } else {
    headerPayload = { format: headerFormat.value.id };
    if (headerFormat.value.id === 'TEXT') headerPayload.text = headerText.value;
    if (headerFormat.value.id === 'IMAGE') {
      headerPayload.handle = headerHandle.value;
      headerPayload.url = headerImageUrl.value;
    }
  }

  const buttonsPayload = buttons.value.map(b => {
    if (b.kind === 'URL') return { type: 'URL', text: b.text, url: b.url };
    if (b.kind === 'URL_VARIABLE') {
      const url = b.url.includes('{{1}}') ? b.url : `${b.url}{{1}}`;
      return { type: 'URL', text: b.text, url, example: b.example };
    }
    if (b.kind === 'PHONE_NUMBER')
      return { type: 'PHONE_NUMBER', text: b.text, phone_number: b.phone };
    if (b.kind === 'ORDER_DETAILS')
      return { type: 'ORDER_DETAILS', text: b.text };
    if (b.kind === 'CATALOG') return { type: 'CATALOG', text: 'View catalog' };
    if (b.kind === 'SPM') {
      if (b.useUrl) {
        const spmUrl = { type: 'SPM_URL', text: b.text || 'Ver produto', url: b.url };
        if (b.example?.trim()) spmUrl.example = b.example;
        return spmUrl;
      }
      return { type: 'SPM', text: 'View' };
    }
    if (b.kind === 'MPM') return { type: 'MPM', text: 'View items' };
    if (b.kind === 'CAROUSEL') {
      const carouselPayload = { type: 'CAROUSEL', cards: b.cards || [] };
      if (b.useUrl) carouselPayload.useUrl = true;
      return carouselPayload;
    }
    return { type: 'QUICK_REPLY', text: b.text };
  });

  const examplesPayload = bodyVariables.value.map(
    v => bodyExamples.value[v] || ''
  );

  return {
    template: {
      name: name.value,
      category: category.value.id,
      language: language.value.id,
      body: body.value,
      body_examples: examplesPayload,
      footer: footer.value,
      header: headerPayload,
      buttons: buttonsPayload,
      flow_slug: props.flowSlug,
    },
  };
}

async function submit() {
  errorMessage.value = '';
  isSubmitting.value = true;
  try {
    const { data } = await axios.post(
      `/api/v1/accounts/${accountId.value}/inboxes/${props.inboxId}/whatsapp_templates`,
      buildPayload()
    );
    useAlert(t('WHATSAPP_TEMPLATES.CREATE.ALERTS.SUBMIT_SUCCESS'));
    emit('created', data.template);
    emit('close');
  } catch (err) {
    errorMessage.value =
      err.response?.data?.error ||
      err.message ||
      t('WHATSAPP_TEMPLATES.CREATE.ALERTS.SUBMIT_ERROR');
  } finally {
    isSubmitting.value = false;
  }
}

function insertVariable() {
  const next = bodyVariables.value.length + 1;
  body.value = `${body.value}{{${next}}}`;
}

const showEmojiPicker = ref(false);
function onEmojiClick(emoji) {
  body.value = (body.value || '') + emoji;
  showEmojiPicker.value = false;
}

function variableLabel(v) {
  return `{{${v}}}`;
}
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('WHATSAPP_TEMPLATES.CREATE.TITLE')"
    width="5xl"
    overflow-y-auto
    :show-confirm-button="false"
    :show-cancel-button="false"
    @close="emit('close')"
  >
    <template #header-actions>
      <Button
        variant="ghost"
        color="slate"
        size="sm"
        icon="i-lucide-x"
        class="self-center"
        @click="emit('close')"
      />
    </template>
    <div class="flex flex-col lg:flex-row gap-4 w-full">
      <!-- Wizard -->
      <div
        class="flex-1 grid grid-cols-1 lg:grid-cols-8 lg:divide-x lg:divide-n-weak rounded-xl border border-n-weak"
      >
        <!-- Sidebar steps -->
        <div class="hidden lg:block col-span-2 h-fit py-6 px-4">
          <div
            v-for="step in STEPS"
            :key="step.id"
            class="flex items-start gap-4 relative after:content-[''] after:absolute after:w-0.5 after:h-full after:top-5 ltr:after:left-4 rtl:after:right-4 before:content-[''] before:absolute before:w-0.5 before:h-4 before:top-0 ltr:before:left-4 rtl:before:right-4 last:after:hidden last:before:hidden after:bg-n-slate-3 before:bg-n-slate-3"
          >
            <div
              class="rounded-2xl flex-shrink-0 size-8 border-2 border-n-slate-3 flex items-center justify-center z-10 transition-all duration-300 ease-in-out"
              :class="{
                'bg-n-slate-3':
                  currentStep === step.id || step.id < currentStep,
                'bg-n-background': step.id > currentStep,
              }"
            >
              <span
                v-if="step.id >= currentStep"
                class="text-xs font-bold transition-colors duration-300"
                :class="
                  currentStep === step.id ? 'text-n-blue-11' : 'text-n-slate-11'
                "
              >
                {{ step.id }}
              </span>
              <span v-else class="i-lucide-check text-n-slate-11 size-4" />
            </div>
            <div class="flex flex-col items-start gap-1 pb-6 pt-1">
              <h3
                class="text-sm font-medium leading-tight"
                :class="
                  currentStep === step.id ? 'text-n-blue-11' : 'text-n-slate-12'
                "
              >
                {{
                  step.id === 3 && hasCarouselButton
                    ? t('WHATSAPP_TEMPLATES.CREATE.STEPS.CARDS_TITLE')
                    : step.title
                }}
              </h3>
              <p class="m-0 text-xs text-n-slate-10 leading-snug">
                {{
                  step.id === 3 && hasCarouselButton
                    ? t('WHATSAPP_TEMPLATES.CREATE.STEPS.CARDS_BODY')
                    : step.body
                }}
              </p>
            </div>
          </div>
        </div>

        <!-- Step content -->
        <div class="col-span-6 w-full p-6 flex flex-col gap-4">
          <!-- Step 1: Configuração -->
          <div v-show="currentStep === 1" class="flex flex-col gap-3 max-w-lg">
            <Input
              v-model="name"
              :label="t('WHATSAPP_TEMPLATES.CREATE.FIELDS.NAME')"
            />
            <div class="flex flex-col gap-1">
              <label class="text-sm font-medium text-n-slate-12">
                {{ t('WHATSAPP_TEMPLATES.CREATE.FIELDS.CATEGORY') }}
              </label>
              <div class="multiselect-wrap--small">
                <MultiselectDropdown
                  :options="CATEGORY_OPTIONS"
                  :selected-item="category"
                  :multiselector-placeholder="
                    t('WHATSAPP_TEMPLATES.CREATE.FIELDS.SELECT')
                  "
                  :input-placeholder="
                    t('WHATSAPP_TEMPLATES.CREATE.FIELDS.SEARCH')
                  "
                  :has-thumbnail="false"
                  @select="category = $event"
                />
              </div>
            </div>
            <div class="flex flex-col gap-1">
              <label class="text-sm font-medium text-n-slate-12">
                {{ t('WHATSAPP_TEMPLATES.CREATE.FIELDS.LANGUAGE') }}
              </label>
              <div class="multiselect-wrap--small">
                <MultiselectDropdown
                  :options="LANGUAGE_OPTIONS"
                  :selected-item="language"
                  :multiselector-placeholder="
                    t('WHATSAPP_TEMPLATES.CREATE.FIELDS.SELECT')
                  "
                  :input-placeholder="
                    t('WHATSAPP_TEMPLATES.CREATE.FIELDS.SEARCH')
                  "
                  :has-thumbnail="false"
                  @select="language = $event"
                />
              </div>
            </div>
            <div class="flex flex-col gap-1">
              <label class="text-sm font-medium text-n-slate-12">
                {{ t('WHATSAPP_TEMPLATES.CREATE.TEMPLATE_FORMAT') }}
              </label>
              <div class="multiselect-wrap--small">
                <MultiselectDropdown
                  :options="TEMPLATE_FORMAT_OPTIONS"
                  :selected-item="templateFormat"
                  :multiselector-placeholder="
                    t('WHATSAPP_TEMPLATES.CREATE.FIELDS.SELECT')
                  "
                  :input-placeholder="
                    t('WHATSAPP_TEMPLATES.CREATE.FIELDS.SEARCH')
                  "
                  :has-thumbnail="false"
                  @select="templateFormat = $event"
                />
              </div>
            </div>
          </div>

          <!-- Step 2: Conteúdo -->
          <div v-show="currentStep === 2" class="flex flex-col gap-3 max-w-lg">
            <div class="flex flex-col gap-1">
              <label class="text-sm font-medium text-n-slate-12">
                {{ t('WHATSAPP_TEMPLATES.CREATE.FIELDS.HEADER') }}
              </label>
              <div class="multiselect-wrap--small">
                <MultiselectDropdown
                  :options="HEADER_OPTIONS"
                  :selected-item="headerFormat"
                  :disabled="hasSpmButton || hasCarouselButton || hasMpmButton"
                  :multiselector-placeholder="
                    t('WHATSAPP_TEMPLATES.CREATE.FIELDS.SELECT')
                  "
                  :input-placeholder="
                    t('WHATSAPP_TEMPLATES.CREATE.FIELDS.SEARCH')
                  "
                  :has-thumbnail="false"
                  @select="headerFormat = $event"
                />
              </div>
              <Input
                v-if="!hasSpmButton && headerFormat.id === 'TEXT'"
                v-model="headerText"
                :placeholder="
                  t('WHATSAPP_TEMPLATES.CREATE.FIELDS.HEADER_TEXT_PLACEHOLDER')
                "
                :maxlength="60"
              />
              <div
                v-if="!hasSpmButton && headerFormat.id === 'IMAGE'"
                class="flex items-center gap-2"
              >
                <input
                  type="file"
                  accept="image/*"
                  class="text-xs"
                  @change="handleImageChange"
                />
                <span v-if="uploadInProgress" class="text-xs text-n-slate-10">
                  {{ t('WHATSAPP_TEMPLATES.CREATE.FIELDS.UPLOADING') }}
                </span>
              </div>
            </div>

            <div class="flex flex-col gap-1">
              <label class="text-sm font-medium text-n-slate-12">
                {{ t('WHATSAPP_TEMPLATES.CREATE.FIELDS.BODY') }}
              </label>
              <div
                class="bg-n-solid-1 rounded-lg outline outline-1 outline-n-weak focus-within:outline-n-brand transition-all"
              >
                <textarea
                  v-model="body"
                  :placeholder="
                    t('WHATSAPP_TEMPLATES.CREATE.FIELDS.BODY_PLACEHOLDER')
                  "
                  class="w-full px-3 pt-2 pb-1 min-h-[120px] text-sm bg-transparent resize-none outline-none text-n-slate-12 placeholder:text-n-slate-9"
                  maxlength="1024"
                />
                <div
                  class="flex items-center gap-0.5 px-2 py-1 border-t border-n-weak"
                >
                  <div
                    v-on-click-outside="() => (showEmojiPicker = false)"
                    class="relative"
                  >
                    <button
                      type="button"
                      class="inline-flex items-center justify-center size-7 rounded-lg text-n-slate-11 hover:bg-n-slate-9/10 transition-colors"
                      @click="showEmojiPicker = !showEmojiPicker"
                    >
                      <span class="i-ph-smiley-sticker size-4 flex-shrink-0" />
                    </button>
                    <EmojiInput
                      v-if="showEmojiPicker"
                      class="absolute bottom-full mb-1 ltr:left-0 rtl:right-0 z-50"
                      :on-click="onEmojiClick"
                    />
                  </div>
                  <button
                    type="button"
                    class="inline-flex items-center gap-1 px-2 h-7 rounded-lg text-xs text-n-blue-11 hover:bg-n-blue-3 transition-colors"
                    @click="insertVariable"
                  >
                    {{ t('WHATSAPP_TEMPLATES.CREATE.FIELDS.ADD_VARIABLE') }}
                  </button>
                  <span class="ml-auto text-[10px] text-n-slate-9">
                    {{ `${body.length}/1024` }}
                  </span>
                </div>
              </div>
            </div>

            <div v-if="bodyVariables.length" class="flex flex-col gap-1.5">
              <span class="text-xs font-medium text-n-slate-11">
                {{ t('WHATSAPP_TEMPLATES.CREATE.FIELDS.VARIABLE_EXAMPLES') }}
              </span>
              <div
                v-for="v in bodyVariables"
                :key="v"
                class="flex items-center gap-2"
              >
                <span class="text-xs font-mono text-n-slate-11 w-12 shrink-0">
                  {{ variableLabel(v) }}
                </span>
                <Input
                  :model-value="bodyExamples[v] || ''"
                  :placeholder="
                    t(
                      'WHATSAPP_TEMPLATES.CREATE.FIELDS.VARIABLE_EXAMPLE_PLACEHOLDER'
                    )
                  "
                  class="flex-1"
                  @update:model-value="bodyExamples[v] = $event"
                />
              </div>
            </div>

            <Input
              v-model="footer"
              :label="t('WHATSAPP_TEMPLATES.CREATE.FIELDS.FOOTER')"
              :placeholder="
                t('WHATSAPP_TEMPLATES.CREATE.FIELDS.FOOTER_PLACEHOLDER')
              "
              :maxlength="60"
            />
          </div>

          <!-- Step 3: Botões -->
          <div v-show="currentStep === 3" class="flex flex-col gap-3 max-w-lg">
            <div class="flex items-center justify-between">
              <span class="text-sm font-medium text-n-slate-12">
                {{
                  hasCarouselButton
                    ? t('WHATSAPP_TEMPLATES.CREATE.STEPS.CARDS_TITLE')
                    : t('WHATSAPP_TEMPLATES.CREATE.FIELDS.BUTTONS')
                }}
              </span>
              <Button
                v-if="
                  buttons.length < 3 &&
                  !blockAddButton &&
                  templateFormat.id === 'STANDARD'
                "
                size="sm"
                variant="ghost"
                color="slate"
                icon="i-lucide-plus"
                @click="addButton"
              >
                {{ t('WHATSAPP_TEMPLATES.CREATE.FIELDS.ADD') }}
              </Button>
            </div>
            <div
              v-for="(btn, idx) in buttons"
              :key="idx"
              class="flex flex-col gap-2 p-3 border border-n-weak rounded-lg"
            >
              <div
                v-if="templateFormat.id === 'STANDARD'"
                class="flex items-center justify-between gap-2"
              >
                <div class="multiselect-wrap--small flex-1">
                  <MultiselectDropdown
                    :options="BUTTON_TYPE_OPTIONS"
                    :selected-item="
                      BUTTON_TYPE_OPTIONS.find(o => o.id === btn.kind)
                    "
                    :disabled="false"
                    :multiselector-placeholder="
                      t(
                        'WHATSAPP_TEMPLATES.CREATE.FIELDS.BUTTON_TYPE_PLACEHOLDER'
                      )
                    "
                    :input-placeholder="
                      t('WHATSAPP_TEMPLATES.CREATE.FIELDS.SEARCH')
                    "
                    :has-thumbnail="false"
                    @select="onButtonKindChange(idx, $event.id)"
                  />
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  color="ruby"
                  icon="i-lucide-trash-2"
                  @click="removeButton(idx)"
                />
              </div>
              <!-- SPM: toggle between default button and URL checkout -->
              <template v-if="templateFormat.id === 'SPM' && btn.kind === 'SPM'">
                <div class="flex flex-col gap-1">
                  <label class="text-xs font-medium text-n-slate-11">
                    {{ t('WHATSAPP_TEMPLATES.CREATE.BUTTON_TYPES.BTN_MODE_LABEL') }}
                  </label>
                  <div class="multiselect-wrap--small">
                    <MultiselectDropdown
                      :options="BUTTON_MODE_OPTIONS"
                      :selected-item="BUTTON_MODE_OPTIONS.find(o => o.id === (btn.useUrl ? 'url' : 'default'))"
                      :multiselector-placeholder="t('WHATSAPP_TEMPLATES.CREATE.FIELDS.SELECT')"
                      :input-placeholder="t('WHATSAPP_TEMPLATES.CREATE.FIELDS.SEARCH')"
                      :has-thumbnail="false"
                      @select="btn.useUrl = $event.id === 'url'"
                    />
                  </div>
                </div>
                <div v-if="!btn.useUrl" class="flex flex-col gap-1.5">
                  <p class="text-xs text-n-slate-9">
                    {{ t('WHATSAPP_TEMPLATES.CREATE.BUTTON_TYPES.FIXED_BUTTON_INFO') }}
                  </p>
                  <div class="flex flex-col gap-1">
                    <label class="text-xs font-medium text-n-slate-11">{{ t('WHATSAPP_TEMPLATES.CREATE.FIELDS.BUTTON_TEXT_PLACEHOLDER') }}</label>
                    <Input :model-value="FIXED_BUTTON_TEXT['SPM'] || ''" disabled />
                  </div>
                </div>
                <template v-else>
                  <div class="flex flex-col gap-1">
                    <label class="text-xs font-medium text-n-slate-11">{{ t('WHATSAPP_TEMPLATES.CREATE.FIELDS.BUTTON_TEXT_PLACEHOLDER') }}</label>
                    <Input v-model="btn.text" placeholder="Finalizar compra" :maxlength="25" />
                  </div>
                  <div class="flex flex-col gap-1">
                    <div class="flex items-center gap-1">
                      <label class="text-xs font-medium text-n-slate-11">{{ t('WHATSAPP_TEMPLATES.CREATE.BUTTON_TYPES.SPM_URL_LABEL') }}</label>
                      <span
                        v-tooltip.top="{ content: t('WHATSAPP_TEMPLATES.CREATE.BUTTON_TYPES.URL_VARIABLE_HINT'), container: 'dialog' }"
                        class="i-ph-info size-3.5 text-n-slate-9 cursor-help"
                      />
                    </div>
                    <Input v-model="btn.url" :placeholder="t('WHATSAPP_TEMPLATES.CREATE.FIELDS.URL_PLACEHOLDER')" />
                  </div>
                  <div v-if="btn.url?.includes('{{1}}')" class="flex flex-col gap-1">
                    <label class="text-xs font-medium text-n-slate-11">{{ t('WHATSAPP_TEMPLATES.CREATE.FIELDS.URL_SAMPLE_PLACEHOLDER') }}</label>
                    <Input v-model="btn.example" :placeholder="t('WHATSAPP_TEMPLATES.CREATE.FIELDS.URL_PLACEHOLDER')" />
                  </div>
                </template>
              </template>
              <!-- CATALOG / MPM: fixed button only -->
              <div
                v-else-if="templateFormat.id !== 'STANDARD' && btn.kind !== 'CAROUSEL'"
                class="flex flex-col gap-1.5"
              >
                <p class="text-xs text-n-slate-9">
                  {{ t('WHATSAPP_TEMPLATES.CREATE.BUTTON_TYPES.FIXED_BUTTON_INFO') }}
                </p>
                <div class="flex flex-col gap-1">
                  <label class="text-xs font-medium text-n-slate-11">{{ t('WHATSAPP_TEMPLATES.CREATE.FIELDS.BUTTON_TEXT_PLACEHOLDER') }}</label>
                  <Input :model-value="FIXED_BUTTON_TEXT[btn.kind] || ''" disabled />
                </div>
              </div>
              <!-- CAROUSEL -->
              <div v-if="btn.kind === 'CAROUSEL'" class="flex flex-col gap-2">
                <!-- Button type selector -->
                <div class="flex flex-col gap-1">
                  <label class="text-xs font-medium text-n-slate-11">
                    {{ t('WHATSAPP_TEMPLATES.CREATE.BUTTON_TYPES.BTN_MODE_LABEL') }}
                  </label>
                  <div class="multiselect-wrap--small">
                    <MultiselectDropdown
                      :options="BUTTON_MODE_OPTIONS"
                      :selected-item="BUTTON_MODE_OPTIONS.find(o => o.id === (btn.useUrl ? 'url' : 'default'))"
                      :multiselector-placeholder="t('WHATSAPP_TEMPLATES.CREATE.FIELDS.SELECT')"
                      :input-placeholder="t('WHATSAPP_TEMPLATES.CREATE.FIELDS.SEARCH')"
                      :has-thumbnail="false"
                      @select="btn.useUrl = $event.id === 'url'"
                    />
                  </div>
                </div>
                <!-- Cards -->
                <div class="flex items-center justify-between">
                  <Button
                    v-if="(btn.cards || []).length < 10"
                    size="xs"
                    variant="ghost"
                    color="slate"
                    icon="i-lucide-plus"
                    @click="addCarouselCard(idx)"
                  >
                    {{ t('WHATSAPP_TEMPLATES.CREATE.BUTTON_TYPES.CAROUSEL_ADD_CARD') }}
                  </Button>
                </div>
                <div
                  v-for="(card, cardIdx) in btn.cards || []"
                  :key="cardIdx"
                  class="flex flex-col gap-1 p-2 border border-n-weak rounded-lg"
                >
                  <div class="flex items-center justify-between">
                    <span class="text-xs font-medium text-n-slate-11">
                      {{ t('WHATSAPP_TEMPLATES.CREATE.BUTTON_TYPES.CAROUSEL_CARD', { n: cardIdx + 1 }) }}
                    </span>
                    <Button
                      v-if="(btn.cards || []).length > 2"
                      size="xs"
                      variant="ghost"
                      color="ruby"
                      icon="i-lucide-trash-2"
                      @click="removeCarouselCard(idx, cardIdx)"
                    />
                  </div>
                  <textarea
                    :value="card.body"
                    rows="2"
                    maxlength="160"
                    class="w-full px-2 py-1.5 rounded-md bg-n-solid-1 outline outline-1 outline-n-weak focus:outline-n-brand text-sm text-n-slate-12 placeholder:text-n-slate-9 resize-none"
                    :placeholder="t('WHATSAPP_TEMPLATES.CREATE.BUTTON_TYPES.CAROUSEL_CARD_BODY', { n: cardIdx + 1 })"
                    @input="updateCarouselCard(idx, cardIdx, $event.target.value)"
                  />
                  <template v-if="btn.useUrl">
                    <div class="flex flex-col gap-1">
                      <label class="text-xs font-medium text-n-slate-11">{{ t('WHATSAPP_TEMPLATES.CREATE.FIELDS.BUTTON_TEXT_PLACEHOLDER') }}</label>
                      <Input v-model="card.buttonText" placeholder="Finalizar compra" :maxlength="25" />
                    </div>
                    <div class="flex flex-col gap-1">
                      <div class="flex items-center gap-1">
                        <label class="text-xs font-medium text-n-slate-11">{{ t('WHATSAPP_TEMPLATES.CREATE.BUTTON_TYPES.SPM_URL_LABEL') }}</label>
                        <span
                          v-tooltip.top="{ content: t('WHATSAPP_TEMPLATES.CREATE.BUTTON_TYPES.URL_VARIABLE_HINT'), container: 'dialog' }"
                          class="i-ph-info size-3.5 text-n-slate-9 cursor-help"
                        />
                      </div>
                      <Input
                        v-model="card.url"
                        :placeholder="t('WHATSAPP_TEMPLATES.CREATE.FIELDS.URL_PLACEHOLDER')"
                      />
                    </div>
                    <div v-if="card.url?.includes('{{1}}')" class="flex flex-col gap-1">
                      <label class="text-xs font-medium text-n-slate-11">{{ t('WHATSAPP_TEMPLATES.CREATE.FIELDS.URL_SAMPLE_PLACEHOLDER') }}</label>
                      <Input v-model="card.example" :placeholder="t('WHATSAPP_TEMPLATES.CREATE.FIELDS.URL_PLACEHOLDER')" />
                    </div>
                  </template>
                </div>
              </div>
              <!-- Text input for non-fixed types -->
              <Input
                v-if="!FIXED_TEXT_BUTTON_TYPES.includes(btn.kind)"
                v-model="btn.text"
                :placeholder="
                  t('WHATSAPP_TEMPLATES.CREATE.FIELDS.BUTTON_TEXT_PLACEHOLDER')
                "
                :maxlength="25"
              />
              <Input
                v-if="btn.kind === 'URL' || btn.kind === 'URL_VARIABLE'"
                v-model="btn.url"
                :placeholder="
                  t('WHATSAPP_TEMPLATES.CREATE.FIELDS.URL_PLACEHOLDER')
                "
              />
              <Input
                v-if="btn.kind === 'URL_VARIABLE'"
                v-model="btn.example"
                :placeholder="
                  t('WHATSAPP_TEMPLATES.CREATE.FIELDS.URL_SAMPLE_PLACEHOLDER')
                "
              />
              <Input
                v-if="btn.kind === 'PHONE_NUMBER'"
                v-model="btn.phone"
                :placeholder="
                  t('WHATSAPP_TEMPLATES.CREATE.FIELDS.PHONE_PLACEHOLDER')
                "
              />
            </div>
            <p v-if="!buttons.length" class="text-xs text-n-slate-9">
              {{ t('WHATSAPP_TEMPLATES.CREATE.FIELDS.NO_BUTTONS') }}
            </p>
          </div>

          <!-- Step 4: Revisão -->
          <div v-show="currentStep === 4" class="flex flex-col gap-2">
            <p class="text-sm text-n-slate-11">
              {{ t('WHATSAPP_TEMPLATES.CREATE.REVIEW_PREFIX') }}
              <strong class="text-n-slate-12">
                {{ t('WHATSAPP_TEMPLATES.CREATE.ACTIONS.SUBMIT') }}
              </strong>
              {{ t('WHATSAPP_TEMPLATES.CREATE.REVIEW_SUFFIX') }}
            </p>
          </div>

          <div
            v-if="errorMessage"
            class="text-sm text-n-ruby-11 bg-n-ruby-3 rounded p-2"
          >
            {{ errorMessage }}
          </div>

          <!-- Navegação -->
          <div class="flex items-center gap-3 mt-4">
            <Button
              v-if="currentStep < 4"
              :label="t('WHATSAPP_TEMPLATES.CREATE.ACTIONS.NEXT')"
              :disabled="!canProceed"
              @click="goNext"
            />
            <Button
              v-else
              :label="t('WHATSAPP_TEMPLATES.CREATE.ACTIONS.SUBMIT')"
              :is-loading="isSubmitting"
              :disabled="isSubmitting"
              @click="submit"
            />
            <Button
              variant="faded"
              color="slate"
              :label="
                currentStep === 1
                  ? t('WHATSAPP_TEMPLATES.CREATE.ACTIONS.CANCEL')
                  : t('WHATSAPP_TEMPLATES.CREATE.ACTIONS.BACK')
              "
              @click="goBack"
            />
          </div>
        </div>
      </div>

      <!-- Preview persistente -->
      <div class="w-full lg:w-80 shrink-0">
        <WhatsappTemplatePreview
          :header="previewHeader"
          :body="body"
          :footer="footer"
          :buttons="previewButtons"
          :header-preview-url="headerImagePreview"
        />
      </div>
    </div>
  </Dialog>
</template>
